import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { useOrganizationId } from '../auth/hooks';
import { DatePickerInput } from '../components/DatePickerInput';
import { FormInput } from '../components/FormInput';
import { useToast } from '../components/ToastProvider';
import type { ServiceLog } from '../models';
import './FormPages.css';

type FormData = {
  carId: string;
  date: string;
  mileage: string;
  nextServiceKm: string;
  description: string;
  issuesFound: string;
  nextServiceDate: string;
};

export default function ServiceForm() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { state, serviceOperations } = useAppContext();
  const organizationId = useOrganizationId();
  const isEditMode = serviceId !== undefined;
  const service = isEditMode ? state.services.find(s => s.id === serviceId) : undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      carId: service?.carId || '',
      date: service?.date || '',
      mileage: service?.mileage?.toString() || '',
      nextServiceKm: service?.nextServiceKm?.toString() || '',
      description: service?.description || '',
      issuesFound: service?.issuesFound || '',
      nextServiceDate: service?.nextServiceDate || '',
    },
    mode: 'onChange'
  });

  const carId = watch('carId');
  const mileage = watch('mileage');
  const nextServiceKm = watch('nextServiceKm');

  useEffect(() => {
    if (service) {
      setValue('carId', service.carId);
      setValue('date', service.date);
      setValue('mileage', service.mileage.toString());
      setValue('description', service.description);
      setValue('issuesFound', service.issuesFound || '');
      setValue('nextServiceDate', service.nextServiceDate || '');
      if (service.nextServiceKm) {
        setValue('nextServiceKm', service.nextServiceKm.toString());
      } else if (service.mileage) {
        const m = service.mileage;
        const nextKm = Math.ceil(m / 5000) * 5000;
        setValue('nextServiceKm', String(nextKm === m ? m + 5000 : nextKm));
      }
    }
  }, [service, setValue]);

  // Auto-suggest next service based on 5,000 km intervals when mileage changes
  useEffect(() => {
    const raw = (mileage || '').toString();
    const m = parseInt(raw.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(m) && m > 0 && (!nextServiceKm || !nextServiceKm.trim())) {
      const nextKm = Math.ceil(m / 5000) * 5000;
      setValue('nextServiceKm', String(nextKm === m ? m + 5000 : nextKm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mileage, setValue]);

  // When a car is selected, prefill mileage with the car's current odometer if empty
  useEffect(() => {
    if (!carId) return;
    const car = state.cars.find(c => c.id === carId);
    if (!car) return;
    const hasMileage = parseInt((mileage || '').replace(/[^0-9]/g, ''), 10) > 0;
    if (!hasMileage && typeof car.mileage === 'number') {
      setValue('mileage', String(car.mileage));
      const m = car.mileage;
      if (m > 0 && (!nextServiceKm || !nextServiceKm.trim())) {
        const nextKm = Math.ceil(m / 5000) * 5000;
        setValue('nextServiceKm', String(nextKm === m ? m + 5000 : nextKm));
      }
    }
  }, [carId, state.cars, mileage, nextServiceKm, setValue]);

  const onSubmit = async (data: FormData) => {
    if (!organizationId) {
      alert('Organization ID is required');
      return;
    }

    if (!data.carId || !data.date || !data.mileage || !data.description) {
      alert('Please fill out all required fields');
      return;
    }

    const sanitizeNumber = (val: string) => {
      const cleaned = (val || '').toString().replace(/[^0-9]/g, '');
      return cleaned.length ? parseInt(cleaned, 10) : NaN;
    };

    const mileageNum = sanitizeNumber(data.mileage);
    if (isNaN(mileageNum)) {
      alert('Please enter a valid odometer value in kilometers');
      return;
    }

    const nextKmNum = data.nextServiceKm ? sanitizeNumber(data.nextServiceKm) : undefined;

    setIsSubmitting(true);
    try {
      const serviceData: Partial<ServiceLog> & { organizationId: string; carId: string; date: string; mileage: number; description: string } = {
        organizationId,
        carId: data.carId,
        date: data.date,
        mileage: mileageNum,
        description: data.description.trim(),
      };
      if (data.issuesFound && data.issuesFound.trim()) serviceData.issuesFound = data.issuesFound.trim();
      if (data.nextServiceDate && data.nextServiceDate.trim()) serviceData.nextServiceDate = data.nextServiceDate.trim();
      if (typeof nextKmNum === 'number' && !isNaN(nextKmNum)) {
        serviceData.nextServiceKm = nextKmNum;
      }

      if (isEditMode && serviceId) {
        await serviceOperations.update(serviceId, serviceData);
      } else {
        await serviceOperations.add(serviceData);
      }
      showToast({ message: isEditMode ? 'Service log updated successfully.' : 'Service log saved successfully.' });
      navigate('/services');
    } catch (err) {
      showToast({ message: 'Failed to save service log: ' + (err as Error).message, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-page">
      <div className="form-page-header">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/services')}>← Back</button>
        <h1 className="form-page-title">{isEditMode ? 'Edit Service Log' : 'Add New Service Log'}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="form-card">
        <div className="form-stack">
          <Controller
            control={control}
            name="carId"
            rules={{ required: 'Car is required' }}
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Select a Car *
                </label>
                <select
                  className="form-select"
                  value={field.value}
                  onChange={field.onChange}
                >
                  <option value="">-- Select a Car --</option>
                  {state.cars.map(car => (
                    <option key={car.id} value={car.id}>
                      {car.year} {car.make} {car.model} ({car.licensePlate})
                    </option>
                  ))}
                </select>
                {errors.carId && <p className="form-error">{errors.carId.message}</p>}
              </div>
            )}
          />

          <Controller
            control={control}
            name="date"
            rules={{ required: 'Service date is required' }}
            render={({ field }) => (
              <DatePickerInput
                label="Service Date"
                value={field.value}
                onChange={field.onChange}
                required
                maximumDate={new Date()}
              />
            )}
          />

          <Controller
            control={control}
            name="mileage"
            rules={{ required: 'Odometer is required' }}
            render={({ field }) => (
              <FormInput
                label="Odometer (km)"
                type="number"
                value={field.value}
                onChange={field.onChange}
                error={errors.mileage?.message}
                required
                placeholder="Odometer reading in kilometers"
              />
            )}
          />

          <Controller
            control={control}
            name="description"
            rules={{ required: 'Description is required' }}
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Description *
                </label>
                <textarea
                  className="form-textarea"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Service description"
                />
                {errors.description && <p className="form-error">{errors.description.message}</p>}
              </div>
            )}
          />

          <Controller
            control={control}
            name="issuesFound"
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Issues Found (optional)
                </label>
                <textarea
                  className="form-textarea form-textarea-sm"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Issues found"
                />
              </div>
            )}
          />

          <Controller
            control={control}
            name="nextServiceKm"
            render={({ field }) => (
              <FormInput
                label="Next Service at (km)"
                type="number"
                value={field.value}
                onChange={field.onChange}
                placeholder="e.g., 100000"
                hint="Default is the next 5,000 km interval"
              />
            )}
          />

          <Controller
            control={control}
            name="nextServiceDate"
            render={({ field }) => (
              <DatePickerInput
                label="Next Service Date (optional)"
                value={field.value}
                onChange={field.onChange}
                placeholder="If you also track by date"
                minimumDate={new Date()}
              />
            )}
          />

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Save Service Log'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/services')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

